import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/db';

export type FeedType = 'all' | 'items' | 'providers';

export interface FeedEntry {
  id: string;
  type: 'item' | 'provider';
  data: any;
}

const DAYS_30_MS = 30 * 24 * 60 * 60 * 1000;
const DAYS_60_MS = 60 * 24 * 60 * 60 * 1000;
const DAYS_90_MS = 90 * 24 * 60 * 60 * 1000;

const shuffleArray = <T,>(arr: T[]): T[] => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const toTimestamp = (entry: FeedEntry): number => {
  const source = entry.data?.created_at || entry.data?.updated_at;
  const ts = source ? new Date(source).getTime() : 0;
  return Number.isFinite(ts) ? ts : 0;
};

const recencyShuffle = (list: FeedEntry[]): FeedEntry[] => {
  const now = Date.now();

  const last30: FeedEntry[] = [];
  const day31to60: FeedEntry[] = [];
  const day61to90: FeedEntry[] = [];
  const older: FeedEntry[] = [];

  for (const entry of list) {
    const age = now - toTimestamp(entry);
    if (age <= DAYS_30_MS) {
      last30.push(entry);
    } else if (age <= DAYS_60_MS) {
      day31to60.push(entry);
    } else if (age <= DAYS_90_MS) {
      day61to90.push(entry);
    } else {
      older.push(entry);
    }
  }

  return [
    ...shuffleArray(last30),
    ...shuffleArray(day31to60),
    ...shuffleArray(day61to90),
    ...shuffleArray(older),
  ];
};

export function useFeedData(feedType: FeedType = 'all') {
  const [entries, setEntries] = useState<FeedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let itemEntries: FeedEntry[] = [];
      let providerEntries: FeedEntry[] = [];

      if (feedType === 'all' || feedType === 'items') {
        const { data, error: itemsError } = await supabase
          .from('items')
          .select(`
            *,
            item_images (*),
            seller_profiles!inner (
              id,
              store_name,
              location,
              is_deactivated,
              rating,
              reviews_count,
              whatsapp,
              telegram,
              messenger,
              phone
            )
          `)
          .eq('seller_profiles.is_deactivated', false)
          .eq('hide_listing', false)
          .order('created_at', { ascending: false });

        if (itemsError) {
          console.error('Error fetching items:', itemsError);
        } else if (data) {
          itemEntries = data.map((item: any) => ({
            id: item.id,
            type: 'item' as const,
            data: item,
          }));
          itemEntries = recencyShuffle(itemEntries);
        }
      }

      if (feedType === 'all' || feedType === 'providers') {
        const { data, error: providersError } = await supabase
          .from('provider_profiles')
          .select(`
            *,
            profiles (
              contact_name
            )
          `)
          .or('is_deactivated.eq.false,is_deactivated.is.null')
          .order('rating', { ascending: false });

        if (providersError) {
          console.error('Error fetching providers:', providersError);
        } else if (data) {
          providerEntries = data.map((provider: any) => ({
            id: provider.id,
            type: 'provider' as const,
            data: provider,
          }));
          providerEntries = recencyShuffle(providerEntries);
        }
      }

      // Combine based on feed type
      if (feedType === 'items') {
        setEntries(itemEntries);
      } else if (feedType === 'providers') {
        setEntries(providerEntries);
      } else {
        // Interleave: 2 items, then 1 provider, repeat
        const combined: FeedEntry[] = [];
        let itemIdx = 0;
        let providerIdx = 0;

        while (itemIdx < itemEntries.length || providerIdx < providerEntries.length) {
          for (let i = 0; i < 3 && itemIdx < itemEntries.length; i++) {
            combined.push(itemEntries[itemIdx++]);
          }
          if (providerIdx < providerEntries.length) {
            combined.push(providerEntries[providerIdx++]);
          }
        }

        setEntries(combined);
      }
    } catch (err: any) {
      console.error('Feed data fetch error:', err);
      setError('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, [feedType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { entries, loading, error, refresh: fetchData };
}

export interface SmartSearchResult {
  keywords_en: string[];
  keywords_am: string[];
  category?: string | null;
  subcategory?: string | null;
  condition?: string | null;
  min_price?: number | null;
  max_price?: number | null;
  intent?: string;
}

/**
 * Search hook for the search overlay — with optional AI-powered smart search
 */
export function useSearchFeed() {
  const [results, setResults] = useState<FeedEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSmartResult, setLastSmartResult] = useState<SmartSearchResult | null>(null);

  /** Basic ilike search (fallback / category-only browsing) */
  const basicSearch = useCallback(async (
    q: string,
    type: 'all' | 'items' | 'providers',
    category?: string,
    condition?: string | null,
    minPrice?: number | null,
    maxPrice?: number | null,
  ) => {
    let itemResults: FeedEntry[] = [];
    let providerResults: FeedEntry[] = [];

    if (type === 'all' || type === 'items') {
      let itemQuery = supabase
        .from('items')
        .select(`
          *,
          item_images (*),
          seller_profiles!inner (
            id, store_name, location, is_deactivated, rating, reviews_count
          )
        `)
        .eq('seller_profiles.is_deactivated', false)
        .eq('hide_listing', false);

      if (q) {
        itemQuery = itemQuery.or(
          `name.ilike.%${q}%,description.ilike.%${q}%,category.ilike.%${q}%,sub_category.ilike.%${q}%,product_number.ilike.%${q}%`
        );
      }
      if (category) {
        itemQuery = itemQuery.ilike('category', `%${category}%`);
      }
      if (condition) {
        itemQuery = itemQuery.eq('condition', condition);
      }
      if (minPrice != null) {
        itemQuery = itemQuery.gte('price', minPrice);
      }
      if (maxPrice != null) {
        itemQuery = itemQuery.lte('price', maxPrice);
      }

      const { data, error: itemsError } = await itemQuery
        .order('created_at', { ascending: false })
        .limit(30);

      if (!itemsError && data) {
        itemResults = data.map((item: any) => ({
          id: item.id,
          type: 'item' as const,
          data: item,
        }));
      }
    }

    if (type === 'all' || type === 'providers') {
      let providerQuery = supabase
        .from('provider_profiles')
        .select(`*, profiles ( contact_name )`)
        .or('is_deactivated.eq.false,is_deactivated.is.null');

      if (q) {
        providerQuery = providerQuery.or(
          `business_name.ilike.%${q}%,category.ilike.%${q}%,short_description.ilike.%${q}%`
        );
      }
      if (category) {
        providerQuery = providerQuery.ilike('category', `%${category}%`);
      }

      const { data, error: providersError } = await providerQuery
        .order('rating', { ascending: false })
        .limit(20);

      if (!providersError && data) {
        providerResults = data.map((provider: any) => ({
          id: provider.id,
          type: 'provider' as const,
          data: provider,
        }));
      }
    }

    return [...itemResults, ...providerResults];
  }, []);

  /** AI-powered smart search: calls edge function then runs expanded queries */
  const smartSearch = useCallback(async (query: string, language: string) => {
    setAiLoading(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('smart-search', {
        body: { query, language },
      });

      if (fnError || !data || data.error) {
        console.warn('Smart search fallback:', fnError || data?.error);
        return null;
      }

      setLastSmartResult(data as SmartSearchResult);
      return data as SmartSearchResult;
    } catch (err) {
      console.warn('Smart search error, falling back:', err);
      return null;
    } finally {
      setAiLoading(false);
    }
  }, []);

  /** Main search function — tries AI first, then falls back */
  const search = useCallback(async (
    query: string,
    type: 'all' | 'items' | 'providers' = 'all',
    category?: string,
    language: string = 'en',
    useAi: boolean = true,
  ) => {
    const q = query.trim();
    if (!q && !category) {
      setResults([]);
      setLastSmartResult(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // If query is long enough for AI and AI is enabled
      if (useAi && q.length >= 2) {
        const aiResult = await smartSearch(q, language);

        if (aiResult) {
          // Run expanded search using AI keywords
          const allKeywords = [...(aiResult.keywords_en || []), ...(aiResult.keywords_am || [])];
          const uniqueKeywords = [...new Set(allKeywords.map(k => k.toLowerCase()))];
          const aiCategory = aiResult.category || category;

          // Query with each keyword and merge (deduplicated)
          const seen = new Set<string>();
          let combined: FeedEntry[] = [];

          // Search with top keywords (limit to 4 to avoid too many queries)
          const searchTerms = uniqueKeywords.slice(0, 4);

          for (const term of searchTerms) {
            const batch = await basicSearch(
              term, type, aiCategory,
              aiResult.condition, aiResult.min_price, aiResult.max_price
            );
            for (const entry of batch) {
              if (!seen.has(entry.id)) {
                seen.add(entry.id);
                combined.push(entry);
              }
            }
          }

          // Also search with original query to not miss exact matches
          const originalBatch = await basicSearch(
            q, type, aiCategory,
            aiResult.condition, aiResult.min_price, aiResult.max_price
          );
          for (const entry of originalBatch) {
            if (!seen.has(entry.id)) {
              seen.add(entry.id);
              combined.push(entry);
            }
          }

          setResults(combined);
          setLoading(false);
          return;
        }
      }

      // Fallback: basic search
      const fallbackResults = await basicSearch(q, type, category);
      setResults(fallbackResults);
    } catch (err: any) {
      console.error('Search error:', err);
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  }, [basicSearch, smartSearch]);

  const clear = useCallback(() => {
    setResults([]);
    setError(null);
    setLastSmartResult(null);
  }, []);

  return { results, loading, aiLoading, error, search, clear, lastSmartResult };
}
